/* Real image uploader backed by Firebase Storage.
   `value` is an image object { url, storagePath, alt } | null.
   `path` is the Storage folder prefix to upload into. */
import { useRef, useState } from "react";
import Icon from "./Icon.jsx";
import { Button, SmartImage, Input } from "./primitives.jsx";
import { uploadImage, deleteImage } from "../data/storage.js";

export default function MediaUploaderField({ value, onChange, path = "site", aspect = "16/9", withAlt = true }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const result = await uploadImage(path, file, setProgress);
      // Remove the previous object on replace.
      if (value?.storagePath) await deleteImage(value.storagePath);
      onChange({ ...result, alt: value?.alt || "" });
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    handleFile(file);
  };

  const remove = async () => {
    if (value?.storagePath) await deleteImage(value.storagePath);
    onChange(null);
  };

  const hiddenInput = (
    <input ref={inputRef} type="file" accept="image/*" hidden onChange={onInputChange} />
  );

  if (uploading) {
    return (
      <div className="media-zone" style={{ aspectRatio: aspect, height: "auto" }}>
        <div className="t-sm">Uploading… {progress}%</div>
        <div style={{ width: 200, height: 6, background: "var(--bg-warm)", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
          <div style={{ width: progress + "%", height: "100%", background: "var(--primary)", transition: "width 100ms" }} />
        </div>
      </div>
    );
  }

  if (value?.url) {
    return (
      <div>
        {hiddenInput}
        <div className="media-zone__preview" style={{ aspectRatio: aspect }}>
          <SmartImage src={value.url} alt={value.alt || ""} />
          <div style={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 6 }}>
            <Button variant="secondary" size="sm" onClick={pick}>Replace</Button>
            <Button variant="ghost" size="sm" onClick={remove} aria-label="Remove"><Icon.X size={14} /></Button>
          </div>
        </div>
        {withAlt && (
          <Input
            style={{ marginTop: 8 }}
            placeholder="Describe this image (for accessibility)"
            value={value.alt || ""}
            onChange={(e) => onChange({ ...value, alt: e.target.value })}
          />
        )}
        {error && <div className="field__error" role="alert"><Icon.AlertCircle size={14} />{error}</div>}
      </div>
    );
  }

  return (
    <div>
      {hiddenInput}
      <div
        className="media-zone"
        onClick={pick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") pick(); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        role="button"
        tabIndex={0}
        style={{ aspectRatio: aspect, height: "auto", minHeight: 160 }}
      >
        <Icon.Upload size={28} />
        <div className="t-sm" style={{ marginTop: 4 }}>Drop image here, or <span style={{ color: "var(--primary)" }}>browse</span></div>
        <div className="t-xs muted">PNG, JPG up to 8MB</div>
      </div>
      {error && <div className="field__error" role="alert"><Icon.AlertCircle size={14} />{error}</div>}
    </div>
  );
}
